import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";
import { SocketContext } from "../../context/Socket/SocketContext";
import {
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Chip,
  Divider,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { AttachFile, Colorize, DeleteOutline, Security } from "@material-ui/icons";
import {
  AI_PROVIDER_MENU_SECTIONS,
  getProviderLabel,
  getApiKeyUi
} from "../../constants/aiProviderCatalog";
import { QueueOptions } from "../QueueOptions";
import SchedulesForm from "../SchedulesForm";
import ConfirmationModal from "../ConfirmationModal";
// ✅ FASE 2: Importar modal de preview de cambios
import ConfigChangePreviewModal from "../ConfigChangePreviewModal";
import KeywordGroupForm from "./KeywordGroupForm";
import { useHistory } from "react-router-dom";
import { useStyles } from "./QueueModalStyles";
import useKeywordGroups from "./useKeywordGroups";
import useQueueCatalogs from "./useQueueCatalogs";
import useQueueForm from "./useQueueForm";
import useQueueSave from "./useQueueSave";
import {
  findSettings,
} from "../../services/queue.api";



const QueueSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  color: Yup.string().min(3, "Too Short!").max(9, "Too Long!").required(),
  greetingEnabled: Yup.boolean(),
  greetingMessage: Yup.string().when("greetingEnabled", {
    is: true,
    then: (schema) =>
      schema.test(
        "greeting-non-empty",
        () => i18n.t("queueModal.form.greetingRequired"),
        (v) => !!(v && String(v).trim())
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const QueueModal = ({ open, onClose, queueId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const socketManager = useContext(SocketContext);
  const history = useHistory();
  
  // ✅ Referencia para guardar opciones temporales
  const queueOptionsRef = useRef();
  
  // ✅ NUEVO: Estado para información de FlowBuilder
  const [flowbuilderInfo, setFlowbuilderInfo] = useState(null);
  const [loadingFlowbuilder, setLoadingFlowbuilder] = useState(false);

  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  
  const [tab, setTab] = useState(0);
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);
  const greetingRef = useRef();
  const queueEditable = true;
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  // ✅ Estado para rastrear el número de opciones de chatbot
  const [chatbotOptionsCount, setChatbotOptionsCount] = useState(0);
  // Catálogos read-only — hook D8.1.4b
  const { prompts, integrations, flowBuilders, loadingFlowBuilders, aiQueues } = useQueueCatalogs({ open, socketManager });

  // Datos + ciclo de vida del form — hook D8.1.4c-1
  const {
    initialState,
    queueData,
    setQueueData,
    attachment,
    setAttachment,
    attachmentFile,
    isLoading,
    setIsLoading,
    dataLoaded,
    setDataLoaded,
    schedules,
    setSchedules,
    selectedPrompt,
    setSelectedPrompt,
    selectedFlowBuilder,
    setSelectedFlowBuilder,
    formikInitialValues,
    handleAttachmentFile,
    deleteMedia,
  } = useQueueForm({ open, queueId, flowBuilders });

  // Palabras clave (grupos de keywords) — hook D8.1.4a
  const {
    keywordGroups,
    setKeywordGroups,
    keywordsEnabled,
    setKeywordsEnabled,
    loadingKeywords,
    editingGroup,
    setEditingGroup,
    showGroupForm,
    setShowGroupForm,
    handleCreateKeywordGroup,
    handleUpdateKeywordGroup,
    handleDeleteKeywordGroup,
  } = useKeywordGroups({ open, queueId, selectedPrompt });

  // Guardado (payload + save + preview/apply + horarios + fallback) — hook D8.1.4c-2
  const {
    previewModalOpen,
    setPreviewModalOpen,
    previewData,
    setPreviewData,
    pendingQueueData,
    setPendingQueueData,
    fallbackValidatingApi,
    setFallbackValidatingApi,
    fallbackApiValidated,
    setFallbackApiValidated,
    handleSaveQueue,
    handleConfirmConfigChange,
    handleSaveSchedules,
    handleValidateFallbackApi,
  } = useQueueSave({ queueId, selectedPrompt, selectedFlowBuilder, schedules, attachment, keywordGroups, queueOptionsRef, setSchedules, setTab });


  

  useEffect(() => {
    findSettings().then(({ data }) => {
      if (Array.isArray(data)) {
        const scheduleType = data.find((d) => d.key === "scheduleType");
        if (scheduleType) {
          setSchedulesEnabled(scheduleType.value === "queue");
        }
      }
    });
  }, []);

  
  // ✅ CORRECCIÓN (30/12/2025): Estado para controlar visibilidad de pestaña de keywords
  const [showKeywordsTab, setShowKeywordsTab] = useState(false);

  
  // ✅ CORRECCIÓN (30/12/2025): Controlar visibilidad de pestaña de keywords
  useEffect(() => {
    const shouldShow = selectedPrompt && selectedPrompt !== null && selectedPrompt !== "";
    setShowKeywordsTab(shouldShow);
    
    // ✅ Si se quita el prompt y estamos en la pestaña de keywords, volver a la primera
    if (!shouldShow && tab > 0) {
      // Use >= to catch both "Palabras Clave" AND "Reacción IA" tabs
      const firstConditionalTabIndex = schedulesEnabled ? 2 : 1;
      if (tab >= firstConditionalTabIndex) {
        setTab(0);
      }
    }
  }, [selectedPrompt, tab, schedulesEnabled]);

  // ✅ Resetear contador de opciones cuando se cierra el modal
  const handleClose = useCallback(() => {
    setChatbotOptionsCount(0);
    setColorPickerModalOpen(false);
    setQueueData(initialState);
    setSelectedPrompt('');
    setSelectedFlowBuilder('');
    setIsLoading(false);
    setDataLoaded(false);
    setFallbackApiValidated(false);
    setFallbackValidatingApi(false);
    onClose();
  }, [initialState, onClose]);

  useEffect(() => {
    if (!open) {
      setColorPickerModalOpen(false);
    }
  }, [open]);




  const handleChangePrompt = (e, setFieldValue) => {
    const value = e.target.value;
    // Si el valor es vacío, establecer como null para deseleccionar
    setSelectedPrompt(value === "" ? '' : value);
    
    // ✅ CORRECCIÓN (30/12/2025): Si se selecciona PROMPT, limpiar INTEGRACIÓN, CHATBOT y FLOWBUILDER
    if (value !== "" && value !== null) {
      setQueueData(prev => ({ ...prev, integrationId: "" }));
      setSelectedFlowBuilder('');
      // ✅ Limpiar campo de integración en Formik
      if (setFieldValue && typeof setFieldValue === 'function') {
        setFieldValue('integrationId', '');
      }
      // ✅ Limpiar opciones de chatbot
      if (queueOptionsRef.current && queueOptionsRef.current.clearAllOptions) {
        queueOptionsRef.current.clearAllOptions();
      }
    }
    
    // ✅ CORRECCIÓN (30/12/2025): Resetear tab si se quita el prompt (pestaña desaparece)
    if (value === "" || value === null) {
      if (tab > 0 && (!schedulesEnabled || tab > 1)) {
        setTab(0);
      }
    }
  };
  
  // ✅ CORRECCIÓN (30/12/2025): Handler para cambio de integración
  const handleChangeIntegration = (value, setFieldValue) => {
    // ✅ Actualizar campo de integración en Formik PRIMERO
    if (setFieldValue && typeof setFieldValue === 'function') {
      setFieldValue('integrationId', value);
    }
    
    // ✅ Actualizar también en queueData para sincronizar
    setQueueData(prev => ({ ...prev, integrationId: value }));
    
    // Si se selecciona INTEGRACIÓN, limpiar PROMPT, CHATBOT y FLOWBUILDER
    if (value !== "" && value !== null) {
      // ✅ Limpiar PROMPT y FLOWBUILDER en estado
      setSelectedPrompt('');
      setSelectedFlowBuilder('');
      
      // ✅ Limpiar opciones de chatbot
      if (queueOptionsRef.current && queueOptionsRef.current.clearAllOptions) {
        queueOptionsRef.current.clearAllOptions();
      }
    }
  };
  
  // ✅ CORRECCIÓN (30/12/2025): Mostrar TODOS los departamentos (no solo los con IA)
  const aiQueuesWithPrompt = aiQueues; // Mostrar TODOS los departamentos

  // [CASO 3] Departamentos sin automatización (Atención Humana) para el selector de contingencia.
  // Excluye cualquier departamento gobernado por Prompt, Integración o FlowBuilder.
  const humanQueues = aiQueues.filter(
    (q) => !q.promptId && !q.integrationId && !q.flowBuilderId
  );
  
  return (
    <div className={classes.root}>
    <ConfirmationModal
        title={i18n.t("queueModal.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      ></ConfirmationModal>
    <Dialog
    maxWidth="md"
    fullWidth={true}
    open={open}
    onClose={handleClose}
    scroll="paper"
    disableEnforceFocus={colorPickerModalOpen}
  >
    <DialogTitle>
      {queueId
        ? i18n.t("queueModal.title.edit")
        : i18n.t("queueModal.title.add")}
       <div style={{ display: "none" }}>
        <input
          type="file"
          ref={attachmentFile}
          onChange={(e) => handleAttachmentFile(e)}
        />
      </div>
    </DialogTitle>
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(_, v) => {
            setTab(v);
          }}
          aria-label="disabled tabs example"
        >
          <Tab label={i18n.t("queueModal.tabs.queueData")} />
          {schedulesEnabled && <Tab label={i18n.t("queueModal.tabs.serviceHours")} />}
          {showKeywordsTab && <Tab label="Palabras Clave" />}
          {/* [CASO 3] Pestaña de contingencia — solo cuando el departamento tiene IA */}
          {showKeywordsTab && <Tab label="Reacción IA" />}
        </Tabs>
        {tab === 0 && (
          <Paper style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper }}>
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress />
              </div>
            ) : (
            <Formik
              enableReinitialize={dataLoaded}
              initialValues={formikInitialValues}
              validationSchema={QueueSchema}
              validateOnMount={false}
              validateOnChange={false}
              validateOnBlur={true}
              onSubmit={async (values, actions) => {
                try {
                  await handleSaveQueue(values, handleClose);
                } catch (err) {
                  toastError(err);
                } finally {
                  actions.setSubmitting(false);
                }
              }}
            >
              {({ touched, errors, isSubmitting, values, setFieldValue }) => {
                // ✅ Calcular hasIntegration, hasPrompt, hasFlowBuilder y hasChatbot dentro del render de Formik usando values
                // ✅ CORRECCIÓN: Asegurar que siempre sean booleanos (no strings)
                const hasPromptInForm = !!(selectedPrompt && selectedPrompt !== null && selectedPrompt !== "");
                const hasIntegrationInForm = !!(values.integrationId && values.integrationId !== null && values.integrationId !== "");
                const hasFlowBuilderInForm = !!(selectedFlowBuilder && selectedFlowBuilder !== null && selectedFlowBuilder !== "");
                const hasChatbotInForm = chatbotOptionsCount > 0;
                
                return (
                <Form>
                  <DialogContent className={classes.dialogContent}>
                    {/* M5: nombre, color, activar saludo */}
                    <div className={classes.formGridThree}>
                      <TextField
                        label={i18n.t("queueModal.form.departmentName")}
                        autoFocus
                        name="name"
                        value={values.name || ""}
                        onChange={(e) => setFieldValue("name", e.target.value)}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        variant="outlined"
                        margin="dense"
                        className={classes.textFieldFull}
                      />
                      <TextField
                        label={i18n.t("queueModal.form.color")}
                        name="color"
                        id="color"
                        value={values.color || ""}
                        onChange={(e) => setFieldValue("color", e.target.value)}
                        error={touched.color && Boolean(errors.color)}
                        helperText={touched.color && errors.color}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <div
                                style={{ backgroundColor: values.color }}
                                className={classes.colorAdorment}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <IconButton
                              size="small"
                              color="default"
                              onClick={() => setColorPickerModalOpen(true)}
                            >
                              <Colorize />
                            </IconButton>
                          ),
                        }}
                        variant="outlined"
                        margin="dense"
                        className={classes.textFieldFull}
                      />
                      <Box display="flex" flexDirection="column" justifyContent="flex-start" pt={0.5}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={values.greetingEnabled}
                              onChange={(e) => setFieldValue("greetingEnabled", e.target.checked)}
                            />
                          }
                          label={i18n.t("queueModal.form.enableGreeting")}
                        />
                        <Typography variant="caption" color="textSecondary" component="span" style={{ marginLeft: 8, marginTop: -4 }}>
                          {values.greetingEnabled
                            ? i18n.t("queueModal.form.greetingEnabledOn")
                            : i18n.t("queueModal.form.greetingEnabledOff")}
                        </Typography>
                      </Box>
                    </div>

                    <ColorPicker
                      open={colorPickerModalOpen}
                      handleClose={() => setColorPickerModalOpen(false)}
                      currentColor={values.color}
                      onChange={(color) => {
                        setFieldValue("color", color);
                      }}
                    />

                    {values.greetingEnabled && (
                      <div className={classes.formGridFull}>
                        <TextField
                          label={i18n.t("queueModal.form.greetingMessage")}
                          multiline
                          inputRef={greetingRef}
                          rows={4}
                          fullWidth
                          name="greetingMessage"
                          value={values.greetingMessage || ""}
                          onChange={(e) => setFieldValue("greetingMessage", e.target.value)}
                          error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                          helperText={touched.greetingMessage && errors.greetingMessage}
                          variant="outlined"
                          margin="dense"
                          className={classes.textFieldFull}
                        />
                      </div>
                    )}

                    {schedulesEnabled && (
                      <div className={classes.formGridFull}>
                        <TextField
                          label={i18n.t("queueModal.form.outOfHoursMessage")}
                          multiline
                          rows={4}
                          fullWidth
                          name="outOfHoursMessage"
                          value={values.outOfHoursMessage || ""}
                          onChange={(e) => setFieldValue("outOfHoursMessage", e.target.value)}
                          error={touched.outOfHoursMessage && Boolean(errors.outOfHoursMessage)}
                          helperText={touched.outOfHoursMessage && errors.outOfHoursMessage}
                          variant="outlined"
                          margin="dense"
                          className={classes.textFieldFull}
                        />
                      </div>
                    )}

                    <Divider style={{ marginTop: 8, marginBottom: 16 }} />

                    <Box className={classes.automationSection}>
                      <Typography className={classes.automationTitle} component="div">
                        <span role="img" aria-hidden="true">🤖</span>
                        {i18n.t("queueModal.automation.title")}
                      </Typography>
                      <Typography className={classes.automationIntro} component="p" style={{ margin: 0 }}>
                        {i18n.t("queueModal.automation.intro")}
                      </Typography>

                      <div className={classes.formGridAutomation}>
                        <FormControl
                          margin="dense"
                          variant="outlined"
                          className={classes.formControl}
                        >
                          <InputLabel>Prompt de IA</InputLabel>
                          <Select
                            labelId="dialog-select-prompt-label"
                            id="dialog-select-prompt"
                            name="promptId"
                            value={selectedPrompt || ""}
                            onChange={(e) => handleChangePrompt(e, setFieldValue)}
                            label="Prompt de IA"
                            disabled={!!(hasIntegrationInForm || hasChatbotInForm || hasFlowBuilderInForm)}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem value="">Sin prompt</MenuItem>
                            {prompts.map((prompt) => (
                              <MenuItem key={prompt.id} value={prompt.id}>
                                {prompt.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl
                          variant="outlined"
                          margin="dense"
                          className={classes.formControl}
                        >
                          <InputLabel id="flowBuilderId-selection-label">
                            FlowBuilder
                          </InputLabel>
                          <Select
                            label="FlowBuilder"
                            name="flowBuilderId"
                            id="flowBuilderId"
                            labelId="flowBuilderId-selection-label"
                            value={selectedFlowBuilder || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSelectedFlowBuilder(value);
                              if (value && value !== "") {
                                setSelectedPrompt("");
                                setFieldValue("integrationId", "");
                                if (queueOptionsRef.current && queueOptionsRef.current.clearOptions) {
                                  queueOptionsRef.current.clearOptions();
                                }
                                setChatbotOptionsCount(0);
                              }
                            }}
                            disabled={!!(loadingFlowBuilders || hasPromptInForm || hasIntegrationInForm || hasChatbotInForm)}
                          >
                            <MenuItem value="">Sin flujo</MenuItem>
                            {flowBuilders.map((flow) => (
                              <MenuItem key={flow.id} value={flow.id}>
                                {flow.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl
                          variant="outlined"
                          margin="dense"
                          className={classes.formControl}
                        >
                          <InputLabel id="integrationId-selection-label">
                            {i18n.t("queueModal.form.integrationId")}
                          </InputLabel>
                          <Field
                            as={Select}
                            label={i18n.t("queueModal.form.integrationId")}
                            name="integrationId"
                            id="integrationId"
                            labelId="integrationId-selection-label"
                            value={values.integrationId !== null && values.integrationId !== undefined ? values.integrationId : ""}
                            disabled={!!(hasPromptInForm || hasChatbotInForm || hasFlowBuilderInForm)}
                            onChange={(e) => handleChangeIntegration(e.target.value, setFieldValue)}
                          >
                            <MenuItem value="">{i18n.t("queueModal.form.noIntegration")}</MenuItem>
                            {integrations.map((integration) => (
                              <MenuItem key={integration.id} value={integration.id}>
                                {integration.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </div>
                    </Box>

                    {/* ✅ CORRECCIÓN (30/12/2025): Mostrar mensaje si chatbot está deshabilitado */}
                    {(hasPromptInForm || hasIntegrationInForm || hasFlowBuilderInForm) && (
                      <div style={{ 
                        padding: '10px', 
                        marginBottom: '10px',
                        backgroundColor: theme.mode === 'dark' ? '#374151' : '#fff3cd',
                        border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#ffc107'}`,
                        borderRadius: '4px',
                        color: theme.mode === 'dark' ? theme.palette.text.secondary : '#856404'
                      }}>
                        <span role="img" aria-label="warning">⚠️</span> Las opciones de chatbot están deshabilitadas porque {hasFlowBuilderInForm ? 'tienes un FlowBuilder seleccionado' : hasPromptInForm ? 'tienes un Prompt de IA seleccionado' : 'tienes una Integración seleccionada'}. Solo puedes usar una automatización a la vez.
                      </div>
                    )}
                    <div style={{ opacity: (hasPromptInForm || hasIntegrationInForm || hasFlowBuilderInForm) ? 0.5 : 1, pointerEvents: (hasPromptInForm || hasIntegrationInForm || hasFlowBuilderInForm) ? 'none' : 'auto' }}>
                      <QueueOptions 
                        queueId={queueId}
                        ref={queueOptionsRef}
                        onOptionAdded={() => {
                          // ✅ Si se agrega una opción de chatbot, limpiar PROMPT e INTEGRACIÓN
                          setSelectedPrompt('');
                          setQueueData(prev => ({ ...prev, integrationId: "" }));
                          
                          // ✅ Limpiar campo de integración en Formik
                          if (setFieldValue && typeof setFieldValue === 'function') {
                            setFieldValue('integrationId', '');
                          }
                          
                          // ✅ Forzar actualización del contador (se actualizará automáticamente con onOptionsChanged)
                          setTimeout(() => {
                            if (queueOptionsRef.current && queueOptionsRef.current.getOptionsCount) {
                              const count = queueOptionsRef.current.getOptionsCount();
                              setChatbotOptionsCount(count);
                            }
                          }, 0);
                        }}
                        onOptionsChanged={(optionsCount) => {
                          // ✅ Actualizar estado local para que el render se actualice
                          setChatbotOptionsCount(optionsCount);
                        }}
                      />
                    </div>
                    
                    {(queueData.mediaPath || attachment) && (
                    <Grid xs={12} item>
                      <Button startIcon={<AttachFile />}>
                        {attachment != null
                          ? attachment.name
                          : queueData.mediaName}
                      </Button>
                      {queueEditable && (
                        <IconButton
                          onClick={() => setConfirmationOpen(true)}
                          color="secondary"
                        >
                          <DeleteOutline />
                        </IconButton>
                      )}
                    </Grid>
                  )}
                  </DialogContent>
                  <DialogActions className={classes.dialogActions}>
                    {/* ✅ BOTÓN ADJUNTAR OCULTO - No se usa */}
                    {/* {!attachment && !queueData.mediaPath && queueEditable && (
                      <Button
                        color="primary"
                        onClick={() => attachmentFile.current.click()}
                        disabled={isSubmitting}
                        variant="outlined"
                      >
                        {i18n.t("queueModal.buttons.attach")}
                      </Button>
                    )} */}
                    
                    <div style={{ flex: 1 }} />
                    
                    <Button
                      className={classes.cancelButton}
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      {i18n.t("queueModal.buttons.cancel")}
                    </Button>
                    
                    <div className={classes.btnWrapper}>
                      <Button
                        className={classes.saveButton}
                        type="submit"
                        disabled={isSubmitting}
                        variant="contained"
                      >
                        {queueId ? i18n.t("queueModal.buttons.okEdit") : i18n.t("queueModal.buttons.okAdd")}
                        {isSubmitting && (
                          <CircularProgress
                            size={24}
                            className={classes.buttonProgress}
                          />
                        )}
                      </Button>
                    </div>
                  </DialogActions>
                </Form>
                );
              }}
            </Formik>
            )}
          </Paper>
        )}
        {/* ✅ PESTAÑA HORARIOS: Solo si schedulesEnabled es true */}
        {tab === 1 && schedulesEnabled && (
          <Paper style={{ padding: 20, backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper }}>
            <SchedulesForm
              loading={false}
              onSubmit={handleSaveSchedules}
              initialValues={schedules}
              labelSaveButton={i18n.t("queueModal.schedules.saveButton")}
            />
          </Paper>
        )}
        {/* ✅ PESTAÑA PALABRAS CLAVE: Índice dinámico según si hay pestaña de horarios */}
        {((schedulesEnabled && tab === 2) || (!schedulesEnabled && tab === 1)) && showKeywordsTab && (
          <Paper style={{ padding: 20, backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper }}>
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ 
                display: 'flex', 
                alignItems: 'center', 
                margin: '0 0 10px 0',
                color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
              }}>
                <span role="img" aria-label="robot">🤖</span> Configuración de Palabras Clave para IA
              </h3>
              <p style={{ 
                color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666', 
                margin: '0 0 20px 0' 
              }}>
                Este departamento tiene IA configurada. Puedes definir palabras clave que activarán 
                la transferencia automática de tickets a este departamento.
              </p>
              
              {/* ✅ NUEVO: Switch para activar/desactivar palabras clave */}
              <div style={{ 
                border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#ddd'}`, 
                borderRadius: '8px', 
                padding: '15px',
                backgroundColor: theme.mode === 'dark' ? '#374151' : '#f9f9f9',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <h4 style={{ 
                      margin: '0 0 5px 0', 
                      fontSize: '16px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      <span role="img" aria-label="target">🎯</span> Activar Detección por Palabras Clave
                    </h4>
                    <p style={{ 
                      margin: '0', 
                      fontSize: '14px', 
                      color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666' 
                    }}>
                      Habilita la transferencia automática basada en palabras clave
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}>
                      <input 
                        type="checkbox" 
                        checked={keywordsEnabled}
                        onChange={(e) => setKeywordsEnabled(e.target.checked)}
                        style={{ 
                          width: '20px', 
                          height: '20px', 
                          marginRight: '8px',
                          cursor: 'pointer'
                        }} 
                      />
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: '500',
                        color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                      }}>
                        {keywordsEnabled ? 'Activado' : 'Desactivado'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* ✅ CONDICIONAL: Solo mostrar si las palabras clave están activadas */}
            {keywordsEnabled && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ 
                  color: theme.mode === 'dark' ? theme.palette.text.primary : '#333',
                  marginBottom: '15px'
                }}>
                  <span role="img" aria-label="memo">📝</span> Grupos de Palabras Clave
                </h4>
                
                {/* ✅ LOADING STATE */}
                {loadingKeywords && (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <CircularProgress size={24} />
                    <p style={{ marginTop: '10px', color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666' }}>
                      Cargando grupos de keywords...
                    </p>
                  </div>
                )}

                {/* ✅ LISTA DE GRUPOS DESDE API */}
                {!loadingKeywords && keywordGroups.length === 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '20px',
                    border: `1px dashed ${theme.mode === 'dark' ? '#4B5563' : '#ddd'}`,
                    borderRadius: '8px',
                    color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666'
                  }}>
                    <p>No hay grupos de keywords configurados aún.</p>
                    <p style={{ fontSize: '12px', marginTop: '5px' }}>
                      Haz clic en "Agregar Nuevo Grupo" para crear uno.
                    </p>
                  </div>
                )}

                {/* ✅ RENDERIZAR GRUPOS DESDE API */}
                {!loadingKeywords && keywordGroups.map((group, index) => {
                  const colors = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b'];
                  const color = colors[index % colors.length];
                  const bgColor = color === '#1976d2' ? '#e3f2fd' : 
                                 color === '#388e3c' ? '#e8f5e8' : 
                                 color === '#f57c00' ? '#fff3e0' :
                                 color === '#7b1fa2' ? '#f3e5f5' : '#fce4ec';
                  
                  const transferQueue = aiQueues.find(q => q.id === group.transferQueueId);
                  
                  return (
                    <div key={group.id} style={{ 
                      border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#ddd'}`, 
                      borderRadius: '8px', 
                      padding: '15px',
                      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f9f9f9',
                      marginBottom: '15px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                        <h5 style={{ 
                          margin: '0', 
                          fontSize: '16px', 
                          color: color 
                        }}>
                          <span role="img" aria-label="label">🏷️</span> {group.groupName}
                          {!group.enabled && (
                            <span style={{ 
                              marginLeft: '10px', 
                              fontSize: '12px', 
                              color: '#999',
                              fontStyle: 'italic'
                            }}>
                              (Desactivado)
                            </span>
                          )}
                        </h5>
                        <div style={{ marginLeft: 'auto' }}>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            style={{ marginRight: '5px' }}
                            onClick={() => {
                              setEditingGroup(group);
                              setShowGroupForm(true);
                            }}
                          >
                            Editar
                          </Button>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="secondary"
                            onClick={() => handleDeleteKeywordGroup(group.id)}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </div>
                      
                      {/* ✅ KEYWORDS DEL GRUPO */}
                      <div style={{ marginBottom: '10px' }}>
                        {group.keywords && group.keywords.length > 0 ? (
                          group.keywords.map((kw, kwIndex) => (
                            <span key={kwIndex} style={{ 
                              display: 'inline-block',
                              backgroundColor: bgColor,
                              color: color,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              margin: '2px',
                              fontSize: '14px'
                            }}>
                              {kw.keyword || kw}
                            </span>
                          ))
                        ) : (
                          <span style={{ 
                            color: theme.mode === 'dark' ? theme.palette.text.secondary : '#999',
                            fontSize: '12px',
                            fontStyle: 'italic'
                          }}>
                            Sin keywords configuradas
                          </span>
                        )}
                      </div>
                      
                      {/* ✅ DEPARTAMENTO DESTINO */}
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <span style={{ 
                          fontSize: '14px', 
                          fontWeight: '500',
                          color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                        }}>
                          Transferir a:
                        </span>
                        <span style={{ 
                          fontSize: '14px',
                          color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666',
                          fontWeight: '500'
                        }}>
                          {transferQueue ? transferQueue.name : `ID: ${group.transferQueueId}`}
                        </span>
                        {group.priority > 0 && (
                          <span style={{ 
                            fontSize: '12px',
                            color: theme.mode === 'dark' ? theme.palette.text.secondary : '#999',
                            backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>
                            Prioridad: {group.priority}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* ✅ BOTÓN AGREGAR NUEVO GRUPO */}
                <div style={{ textAlign: 'center', marginTop: '15px' }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    style={{ 
                      borderStyle: 'dashed',
                      borderColor: '#1976d2',
                      color: '#1976d2'
                    }}
                    disabled={!queueId || isNaN(Number(queueId)) || Number(queueId) <= 0}
                    onClick={() => {
                      if (!queueId || isNaN(Number(queueId)) || Number(queueId) <= 0) {
                        toast.error("Debes guardar el departamento primero antes de crear grupos de keywords");
                        return;
                      }
                      setEditingGroup(null);
                      setShowGroupForm(true);
                    }}
                  >
                    + Agregar Nuevo Grupo
                  </Button>
                  {(!queueId || isNaN(Number(queueId)) || Number(queueId) <= 0) && (
                    <p style={{ 
                      fontSize: '12px', 
                      color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666',
                      marginTop: '8px'
                    }}>
                      Guarda el departamento primero para crear grupos de keywords
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ✅ FORMULARIO PARA CREAR/EDITAR GRUPO */}
            {showGroupForm && (
              <div style={{ 
                border: `2px solid ${theme.mode === 'dark' ? '#4B5563' : '#1976d2'}`, 
                borderRadius: '8px', 
                padding: '20px',
                backgroundColor: theme.mode === 'dark' ? '#374151' : '#f0f7ff',
                marginBottom: '20px'
              }}>
                <h4 style={{ 
                  color: theme.mode === 'dark' ? theme.palette.text.primary : '#333',
                  marginBottom: '15px'
                }}>
                  {editingGroup ? <><span role="img" aria-label="edit">✏️</span> Editar Grupo</> : <><span role="img" aria-label="add">➕</span> Nuevo Grupo de Keywords</>}
                </h4>
                
                <KeywordGroupForm
                  group={editingGroup}
                  aiQueues={aiQueuesWithPrompt}
                  queueId={queueId}
                  onSubmit={async (formData) => {
                    if (editingGroup) {
                      await handleUpdateKeywordGroup(editingGroup.id, formData);
                    } else {
                      await handleCreateKeywordGroup(formData);
                    }
                  }}
                  onCancel={() => {
                    setShowGroupForm(false);
                    setEditingGroup(null);
                  }}
                />
              </div>
            )}

            {/* ✅ CONDICIONAL: Solo mostrar si las palabras clave están activadas */}
            {keywordsEnabled && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <h4 style={{ 
                    color: theme.mode === 'dark' ? theme.palette.text.primary : '#333',
                    marginBottom: '15px'
                  }}>
                    <span role="img" aria-label="chart">📊</span> Estadísticas
                  </h4>
                  <div style={{ 
                    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#ddd'}`, 
                    borderRadius: '8px', 
                    padding: '15px',
                    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f9f9f9'
                  }}>
                    <p style={{ 
                      margin: '5px 0', 
                      fontSize: '14px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      • Transferencias automáticas: <strong>15</strong>
                    </p>
                    <p style={{ 
                      margin: '5px 0', 
                      fontSize: '14px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      • Última activación: <strong>hace 2 horas</strong>
                    </p>
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <h4 style={{ 
                    color: theme.mode === 'dark' ? theme.palette.text.primary : '#333',
                    marginBottom: '15px'
                  }}>
                    <span role="img" aria-label="magnifying glass">🔍</span> Vista Previa
                  </h4>
                  <div style={{ 
                    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#ddd'}`, 
                    borderRadius: '8px', 
                    padding: '15px',
                    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f9f9f9'
                  }}>
                    <p style={{ 
                      margin: '5px 0', 
                      fontSize: '14px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      Usuario escribe: <em>"Quiero comprar un producto"</em>
                    </p>
                    <p style={{ 
                      margin: '5px 0', 
                      fontSize: '14px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      ↓
                    </p>
                    <p style={{ 
                      margin: '5px 0', 
                      fontSize: '14px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      <span role="img" aria-label="robot">🤖</span> Detectado: <strong>"comprar"</strong> → Transferir a <strong>"{queueData.name}"</strong>
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* ✅ MENSAJE CUANDO ESTÁ DESACTIVADO */}
            {!keywordsEnabled && (
              <div style={{ 
                border: `1px solid ${theme.mode === 'dark' ? '#f57c00' : '#ffcc02'}`, 
                borderRadius: '8px', 
                padding: '20px',
                backgroundColor: theme.mode === 'dark' ? '#78350f' : '#fff9c4',
                textAlign: 'center'
              }}>
                <h4 style={{ 
                  margin: '0 0 10px 0', 
                  color: theme.mode === 'dark' ? '#fbbf24' : '#f57c00' 
                }}>
                  <span role="img" aria-label="warning">⚠️</span> Palabras Clave Desactivadas
                </h4>
                <p style={{ 
                  margin: '0', 
                  fontSize: '14px', 
                  color: theme.mode === 'dark' ? theme.palette.text.secondary : theme.palette.text.primary 
                }}>
                  Para configurar palabras clave, primero activa la detección automática arriba.
                </p>
              </div>
            )}
          </Paper>
        )}

        {/* [CASO 3] PESTAÑA REACCIÓN IA: índice dinámico (después de Palabras Clave) */}
        {((schedulesEnabled && tab === 3) || (!schedulesEnabled && tab === 2)) && showKeywordsTab && (
          <Paper style={{ padding: 24, backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper }}>
            <Formik
              enableReinitialize={dataLoaded}
              initialValues={formikInitialValues}
              validationSchema={QueueSchema}
              validateOnMount={false}
              validateOnChange={false}
              validateOnBlur={true}
              onSubmit={async (values, actions) => {
                try { await handleSaveQueue(values, handleClose); }
                catch (err) { toastError(err); }
                finally { actions.setSubmitting(false); }
              }}
            >
              {({ values, setFieldValue, isSubmitting }) => (
                <Form>
                  <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span role="img" aria-label="shield">🛡️</span> Reacción IA — Protocolo de Contingencia
                  </Typography>
                  <Typography variant="body2" style={{ color: theme.palette.text.secondary, marginBottom: 24 }}>
                    Configura el comportamiento del sistema cuando el proveedor de IA falla definitivamente
                    (cuota agotada o caída del servidor). El ticket será transferido automáticamente a un
                    departamento de atención humana y se enviará un mensaje de cortesía al cliente.
                  </Typography>

                  {/* Selector: departamento de respaldo (solo sin automatización) */}
                  <FormControl variant="outlined" fullWidth style={{ marginBottom: 20 }}>
                    <InputLabel>Departamento de Respaldo por Falla de IA</InputLabel>
                    <Select
                      value={values.contingencyQueueId || ""}
                      onChange={(e) => setFieldValue("contingencyQueueId", e.target.value)}
                      label="Departamento de Respaldo por Falla de IA"
                    >
                      <MenuItem value="">
                        <em>Sin departamento de contingencia</em>
                      </MenuItem>
                      {humanQueues.map((q) => (
                        <MenuItem key={q.id} value={String(q.id)}>
                          {q.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" style={{ marginTop: 4, color: theme.palette.text.secondary }}>
                      Solo se listan departamentos sin automatización (Atención Humana).
                    </Typography>
                  </FormControl>

                  {/* Mensaje de cortesía para el cliente */}
                  <TextField
                    label="Mensaje de cortesía al cliente (WhatsApp)"
                    name="contingencyMessage"
                    value={values.contingencyMessage || ""}
                    onChange={(e) => setFieldValue("contingencyMessage", e.target.value)}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Ej: En este momento nuestro sistema está ocupado. Un asesor te atenderá en breve. ¡Gracias por tu paciencia!"
                    style={{ marginBottom: 24 }}
                    helperText="Este texto se enviará automáticamente al cliente por WhatsApp cuando la IA falle de forma definitiva."
                  />

                  {/* ─── Failover cross-family ─── */}
                  <Box
                    style={{
                      border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e5e7eb"}`,
                      borderRadius: 8,
                      padding: 16,
                      backgroundColor: theme.mode === "dark" ? "#111827" : "#f9fafb",
                      marginBottom: 20
                    }}
                  >
                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                      <span role="img" aria-label="fallback">🔀</span> Proveedor de IA de Respaldo (Failover Cross-Family)
                    </Typography>
                    <Typography variant="caption" style={{ color: theme.palette.text.secondary, display: "block", marginBottom: 12 }}>
                      Actívalo para que el sistema intente un segundo proveedor de IA (diferente empresa / API key) antes de activar la contingencia humana.
                      Útil cuando el proveedor principal falla por completo y quieres escalar a otros proveedores como segundo nivel.
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={!!values.aiFallbackEnabled}
                          onChange={(e) => {
                            setFieldValue("aiFallbackEnabled", e.target.checked);
                            if (!e.target.checked) {
                              setFieldValue("aiFallbackProvider", "");
                              setFieldValue("aiFallbackApiKey", "");
                              setFallbackApiValidated(false);
                            }
                          }}
                        />
                      }
                      label="Activar proveedor de respaldo"
                      style={{ marginBottom: values.aiFallbackEnabled ? 16 : 0 }}
                    />

                    {values.aiFallbackEnabled && (
                      <>
                        {/* Selector con el mismo UX que /prompts — grupos + Chip badges */}
                        <FormControl variant="outlined" fullWidth style={{ marginBottom: 8 }}>
                          <InputLabel>Proveedor de Respaldo</InputLabel>
                          <Select
                            value={values.aiFallbackProvider || ""}
                            onChange={(e) => {
                              setFieldValue("aiFallbackProvider", e.target.value);
                              setFallbackApiValidated(false);
                            }}
                            label="Proveedor de Respaldo"
                          >
                            <MenuItem value=""><em>Seleccionar proveedor...</em></MenuItem>
                            {AI_PROVIDER_MENU_SECTIONS.flatMap((section, sectionIdx) => [
                              <MenuItem
                                key={`section-${section.header}`}
                                disabled
                                style={{
                                  backgroundColor: theme.mode === "dark" ? "#374151" : "#f5f5f5",
                                  fontWeight: "bold",
                                  color: theme.mode === "dark" ? "#9CA3AF" : "#333",
                                  marginTop: sectionIdx > 0 ? 8 : 0
                                }}
                              >
                                {section.header}
                              </MenuItem>,
                              ...section.items.map((item) => (
                                <MenuItem key={item.value} value={item.value}>
                                  <Box display="flex" alignItems="center">
                                    <Chip
                                      label={item.chip}
                                      size="small"
                                      className={item.useProviderChipClass ? classes.providerChip : undefined}
                                      style={item.chipStyle || undefined}
                                    />
                                    <Typography style={{ marginLeft: 8 }}>
                                      {getProviderLabel(item.value)}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              ))
                            ])}
                          </Select>
                          <Typography variant="caption" style={{ marginTop: 4, color: theme.palette.text.secondary }}>
                            Selecciona un proveedor de familia diferente al primario del Prompt IA asignado.
                          </Typography>
                        </FormControl>

                        {/* Campo API key con label dinámico según proveedor seleccionado */}
                        {(() => {
                          const keyUi = getApiKeyUi(values.aiFallbackProvider || "");
                          return (
                            <TextField
                              label={keyUi.label}
                              name="aiFallbackApiKey"
                              value={values.aiFallbackApiKey || ""}
                              onChange={(e) => {
                                setFieldValue("aiFallbackApiKey", e.target.value);
                                setFallbackApiValidated(false);
                              }}
                              variant="outlined"
                              fullWidth
                              type="password"
                              placeholder={values.aiFallbackApiKeyPreview ? i18n.t("whatsappModal.tokenKeepPlaceholder") : keyUi.placeholder}
                              helperText={values.aiFallbackApiKeyPreview && !values.aiFallbackApiKey ? i18n.t("whatsappModal.tokenSaved", { preview: values.aiFallbackApiKeyPreview }) : keyUi.helper}
                              style={{ marginBottom: 8 }}
                            />
                          );
                        })()}

                        {/* Botón Validar — mismo patrón que PromptModal */}
                        <Box display="flex" justifyContent="flex-end" marginTop={1}>
                          <Button
                            variant="outlined"
                            size="small"
                            color={fallbackApiValidated ? "primary" : "default"}
                            onClick={() => handleValidateFallbackApi(values.aiFallbackProvider, values.aiFallbackApiKey)}
                            disabled={
                              fallbackValidatingApi ||
                              !values.aiFallbackProvider ||
                              !(values.aiFallbackApiKey && String(values.aiFallbackApiKey).trim())
                            }
                            startIcon={
                              fallbackValidatingApi
                                ? <Security style={{ fontSize: 16 }} />
                                : fallbackApiValidated
                                  ? <Security style={{ color: "green", fontSize: 16 }} />
                                  : <Security style={{ fontSize: 16 }} />
                            }
                          >
                            {fallbackValidatingApi
                              ? "Validando..."
                              : fallbackApiValidated
                                ? "✅ Conexión verificada"
                                : "Validar conexión"}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>

                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      className={classes.saveButton}
                    >
                      {isSubmitting ? <CircularProgress size={20} /> : "Guardar"}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Paper>
        )}
      </Dialog>

      {/* ✅ FASE 2: Modal de preview de cambios de configuración */}
      <ConfigChangePreviewModal
        open={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPendingQueueData(null);
          setPreviewData(null);
        }}
        previewData={previewData}
        onConfirm={() => handleConfirmConfigChange(handleClose)}
        loading={isLoading}
        type="queue"
      />
    </div>
  );
};

export default QueueModal;
